import { useTranslation } from 'react-i18next';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../../api/api';
import ExoDialog from '../../ExoDialog';
import ExoForm from '../../ExoForm';
import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';

export const WorkPackageForm = ({
  phaseId,
  open,
  onClose,
  onChange,
  startDataCollection,
  projectId,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const [defaultValues, setDefaultValues] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef(null);

  useEffect(() => {
    if (!config) return;
    resetDefaultValues();
  }, [config]);

  function resetDefaultValues() {
    const newDeflaultValues = {
      isTimeTrackable: true,
      alertLimit: config.project.defaultWorkPackageAlertThreshold,
    };

    setDefaultValues(newDeflaultValues);
  }

  const initialFields = [
    {
      label: t('Icon'),
      key: 'icon',
      type: 'icon',
      colspan: 2,
    },
    {
      label: t('Name'),
      key: 'title',
      type: 'text',
      colspan: 2,
    },
    {
      label: `${t('Description')}`,
      colspan: 2,
      key: 'description',
      type: 'richText',
    },
    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
    },
    {
      type: 'divider',
      colspan: 2,
    },
    {
      label: t('Allow Time Tracking'),
      key: 'isTimeTrackable',
      type: 'checkbox',
      initialValue: true,
    },
    {
      label: `${t('Estimated Time')}`,
      key: 'estimatedTime',
      type: 'hours',
    },
    {
      label: `${t('Alert Limit')}`,
      key: 'alertLimit',
      type: 'taxRate',
    },
    {
      type: 'divider',
      colspan: 2,
    },

    {
      label: t('Is Billable'),
      key: 'isBillable',
      type: 'checkbox',
    },
    {
      label: t('Billable Position'),
      key: 'projectPosition',
      type: 'billablePosition',
      projectId,
    },
  ];

  const [fields, setFields] = useState(initialFields);

  const billablePositionsFields = [
    {
      label: t('Icon'),
      key: 'icon',
      type: 'icon',
      colspan: 2,
    },
    {
      label: t('Name'),
      key: 'title',
      type: 'text',
      colspan: 2,
    },
    {
      label: `${t('Description')}`,
      colspan: 2,
      key: 'description',
      type: 'richText',
    },
    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
    },
    {
      type: 'divider',
      colspan: 2,
    },
    {
      label: t('Allow Time Tracking'),
      key: 'isTimeTrackable',
      type: 'checkbox',
      disabled: true,
    },
    {
      label: `${t('Estimated Time')}`,
      key: 'estimatedTime',
      type: 'hours',
    },
    {
      label: `${t('Alert Limit')}`,
      key: 'alertLimit',
      type: 'taxRate',
    },
    {
      type: 'divider',
      colspan: 2,
    },

    {
      label: t('Is Billable'),
      key: 'isBillable',
      type: 'checkbox',
      disabled: true,
    },
    {
      label: t('Billable Position'),
      key: 'projectPosition',
      type: 'billablePosition',
      projectId,
    },
  ];

  function handleSubmit(data) {
    const isUpdate = Boolean(data.id);

    const requestData = {
      phase: { id: phaseId },
      ...data,
    };

    DBRequest({
      config,
      path: isUpdate ? `work-packages/${data.id}` : `work-packages`,
      method: isUpdate ? API_PATCH(requestData) : API_POST(requestData),
      onResponse: handleData,
      onLoading: setIsLoading,
    });
  }

  function handleData(data) {
    onClose();
    onChange(data.data);
  }

  const validationSchema = Yup.object().shape({
    icon: Yup.object().shape({ id: Yup.string().required(t('Is required')) }),
    title: Yup.string().required(t('Is required')),
    // projectPosition is always optional - a work package can be billable without a specific position
  });

  function handleChange(data) {
    if (data.projectPosition) {
      setFields(billablePositionsFields);
      const currentData = formRef.current.dataCollection;
      if (currentData.isTimeTrackable === '1' && currentData.isBillable === '1')
        return;
      formRef.current.updateDataCollection({
        isTimeTrackable: '1',
        isBillable: '1',
      });
    } else {
      setFields(initialFields);
    }
  }

  return (
    <ExoDialog open={open} limitWidth>
      <ExoForm
        ref={formRef}
        onChange={handleChange}
        header={
          startDataCollection ? t('Edit Work Package') : t('New Work Package')
        }
        startDataCollection={startDataCollection || defaultValues}
        fields={fields}
        onCancel={onClose}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        isLoading={isLoading}
        noDifferences
        className="grid grid-cols-2 gap-4 pt-2"
      />
    </ExoDialog>
  );
};
